import React from 'react';
import { useResponsiveCards } from '../hooks/useResponsiveLayout';

interface CardLayoutProps {
  children: React.ReactNode;
  cardMinWidth?: number;
  cardMaxWidth?: number;
  aspectRatio?: number;
  gap?: number;
  className?: string;
  title?: string;
  onClick?: () => void;
}

export function CardLayout({
  children,
  cardMinWidth = 280,
  cardMaxWidth = 400,
  aspectRatio = 1.5,
  gap = 16,
  className = '',
  title,
  onClick
}: CardLayoutProps) {
  const { containerStyle } = useResponsiveCards({
    cardMinWidth,
    cardMaxWidth,
    preferredAspectRatio: aspectRatio,
    gap,
  });

  return (
    <div 
      className={`w-full ${onClick ? 'cursor-pointer' : ''} ${className}`}
      style={containerStyle}
      onClick={onClick}
    >
      {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
      {children}
    </div>
  );
}

// Card component with consistent styling
interface CardProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  image?: string;
  actions?: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

export function Card({
  children,
  title,
  subtitle,
  image,
  actions,
  onClick,
  className = ''
}: CardProps) {
  return (
    <div 
      className={`
        bg-slate-800 border border-slate-700 rounded-lg overflow-hidden
        ${onClick ? 'cursor-pointer hover:border-slate-600 transition-colors' : ''}
        ${className}
      `}
      onClick={onClick}
    >
      {/* Card image */}
      {image && (
        <div className="aspect-w-16 aspect-h-9">
          <img 
            src={image} 
            alt={title || 'Card image'} 
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Card content */}
      <div className="p-4">
        {/* Card header */}
        {(title || subtitle) && (
          <div className="mb-3">
            {title && (
              <h3 className="text-lg font-semibold text-white mb-1">{title}</h3>
            )}
            {subtitle && (
              <p className="text-sm text-slate-400">{subtitle}</p>
            )}
          </div>
        )}

        {/* Card body */}
        <div className="mb-4">
          {children}
        </div>

        {/* Card actions */}
        {actions && (
          <div className="flex items-center justify-end space-x-2 pt-3 border-t border-slate-700">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

// Compact card for lists
interface CompactCardProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  icon?: React.ReactNode;
  badge?: React.ReactNode;
  actions?: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

export function CompactCard({
  children,
  title,
  subtitle,
  icon,
  badge,
  actions,
  onClick,
  className = ''
}: CompactCardProps) {
  return (
    <div 
      className={`
        bg-slate-800 border border-slate-700 rounded-lg p-4
        ${onClick ? 'cursor-pointer hover:border-slate-600 transition-colors' : ''}
        ${className}
      `}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3 flex-1 min-w-0">
          {/* Icon */}
          {icon && (
            <div className="flex-shrink-0 text-slate-400">
              {icon}
            </div>
          )}

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2 min-w-0">
                {title && (
                  <h4 className="text-sm font-medium text-white truncate">{title}</h4>
                )}
                {badge && badge}
              </div>
            </div>

            {/* Subtitle */}
            {subtitle && (
              <p className="text-xs text-slate-400 mb-2">{subtitle}</p>
            )}

            {/* Body */}
            <div className="text-sm text-slate-300">
              {children}
            </div>
          </div>
        </div>

        {/* Actions */}
        {actions && (
          <div className="flex-shrink-0 ml-3">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

// Status card with color coding
interface StatusCardProps {
  children: React.ReactNode;
  title?: string;
  status: 'success' | 'warning' | 'error' | 'info' | 'neutral';
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

const STATUS_STYLES = {
  success: 'border-emerald-500/30 bg-emerald-500/10',
  warning: 'border-amber-500/30 bg-amber-500/10',
  error: 'border-red-500/30 bg-red-500/10',
  info: 'border-blue-500/30 bg-blue-500/10',
  neutral: 'border-slate-700 bg-slate-800',
};

const STATUS_TITLE_STYLES = {
  success: 'text-emerald-400',
  warning: 'text-amber-400',
  error: 'text-red-400',
  info: 'text-blue-400',
  neutral: 'text-white',
};

export function StatusCard({
  children,
  title,
  status,
  icon,
  actions,
  onClick,
  className = ''
}: StatusCardProps) {
  return (
    <div 
      className={`
        rounded-lg border p-4
        ${STATUS_STYLES[status]}
        ${onClick ? 'cursor-pointer hover:border-opacity-50 transition-colors' : ''}
        ${className}
      `}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3 flex-1">
          {/* Icon */}
          {icon && (
            <div className={`flex-shrink-0 ${STATUS_TITLE_STYLES[status]}`}>
              {icon}
            </div>
          )}

          {/* Content */}
          <div className="flex-1">
            {/* Title */}
            {title && (
              <h4 className={`text-sm font-medium mb-2 ${STATUS_TITLE_STYLES[status]}`}>
                {title}
              </h4>
            )}

            {/* Body */}
            <div className="text-sm text-slate-300">
              {children}
            </div>
          </div>
        </div>

        {/* Actions */}
        {actions && (
          <div className="flex-shrink-0 ml-3">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

// Metric card for displaying key numbers
interface MetricCardProps {
  title: string;
  value: string | number;
  unit?: string;
  change?: {
    value: number;
    label?: string;
    trend: 'up' | 'down' | 'neutral';
  };
  icon?: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

const TREND_STYLES = {
  up: 'text-emerald-400',
  down: 'text-red-400',
  neutral: 'text-slate-400',
};

export function MetricCard({
  title,
  value,
  unit,
  change,
  icon,
  onClick,
  className = ''
}: MetricCardProps) {
  return (
    <div 
      className={`
        bg-slate-800 border border-slate-700 rounded-lg p-4
        ${onClick ? 'cursor-pointer hover:border-slate-600 transition-colors' : ''}
        ${className}
      `}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {/* Title */}
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">{title}</p>

          {/* Value */}
          <div className="flex items-baseline space-x-1">
            <p className="text-2xl font-bold text-white">
              {typeof value === 'number' ? value.toLocaleString() : value}
            </p>
            {unit && (
              <p className="text-sm text-slate-400">{unit}</p>
            )}
          </div>

          {/* Change indicator */}
          {change && (
            <div className={`flex items-center space-x-1 mt-2 text-sm ${TREND_STYLES[change.trend]}`}>
              <span>{change.value > 0 ? '+' : ''}{change.value}%</span>
              {change.label && (
                <span className="text-slate-500">{change.label}</span>
              )}
            </div>
          )}
        </div>

        {/* Icon */}
        {icon && (
          <div className="flex-shrink-0 text-slate-500">
            {icon}
          </div>
        )}
      </div>
    </div>
  );
}